import { GET_STARTED_FORM_ERRORS } from "@src/constants";
import ClientLogsService from "@src/services/client-logs.service";
import { fetchInsurancePackages } from "@src/services/insurance-package.service";
import logger from "@src/utils/logger";
import { useCallback, useEffect, useState } from "react";

const hasAnyPartnerPackageFilters = (value: Record<string, number[]> | undefined) => {
  return Boolean(value && Object.keys(value).length > 0);
};

const normalizePackageId = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? String(value) : numericValue;
};

/**
 * Fetches insurance packages whenever the address context/state or payment plan changes.
 * Owns its own `insurancePolicies` list and fetch-level `policyError` so the parent
 * form doesn't need to manage that state.
 *
 * @returns `resetPolicies` - call this when the ZIP changes so stale packages are cleared
 *           immediately before the new address fetch resolves.
 */
export const useInsurancePackages = (
  userUuid: string | undefined,
  contextId: string | undefined,
  stateCode: string | undefined,
  selectPlan: string,
  isReferrerFlow: boolean = false,
  selectedPartnerFilteredInsurancePackages?: Record<string, number[]>,
  fetchKey: number = 0,
) => {
  const [insurancePolicies, setInsurancePolicies] = useState<any[]>([]);
  const [policyError, setPolicyError] = useState("");
  const [packageLookupCompleted, setPackageLookupCompleted] = useState(false);

  const resetPolicies = useCallback(() => {
    setInsurancePolicies([]);
    setPolicyError("");
    setPackageLookupCompleted(false);
  }, []);

  useEffect(() => {
    if (!contextId || !stateCode || !userUuid || selectPlan === "self_pay") {
      setInsurancePolicies([]);
      setPolicyError("");
      setPackageLookupCompleted(false);
      return;
    }

    let cancelled = false;

    const loadPackages = async () => {
      const insurancePackages = await fetchInsurancePackages(userUuid, contextId, stateCode, true);
      if (cancelled) return;

      logger.log({
        text: "Fetched insurance packages",
        value: insurancePackages,
        location: "useInsurancePackages",
      });

      if (
        insurancePackages.error ||
        !insurancePackages.message ||
        !Array.isArray(insurancePackages.message)
      ) {
        setPackageLookupCompleted(false);
        if (!insurancePackages.error) {
          ClientLogsService.logError(
            userUuid,
            "useInsurancePackages",
            "Insurance package response was malformed",
            new Error("Expected an insurance package message array"),
            {
              errorType: "TerminalFailure",
              dependencyName: "InsurancePackageAPI",
              functionName: "loadPackages",
            },
          );
        }
        setInsurancePolicies([]);
        setPolicyError(GET_STARTED_FORM_ERRORS.INSURANCE_PACKAGES_FAILED);
        return;
      }

      setPackageLookupCompleted(true);

      if (insurancePackages.message.length === 0) {
        ClientLogsService.logError(
          userUuid,
          "useInsurancePackages",
          "No insurance packages found for the area",
          undefined,
          {
            errorType: "BusinessRuleDenial",
            dependencyName: "InsurancePackageAPI",
            functionName: "loadPackages",
          },
        );
        setInsurancePolicies([]);
        setPolicyError(GET_STARTED_FORM_ERRORS.NO_COVERAGE_IN_AREA);
        return;
      }

      let filteredInsurancePolicies = insurancePackages.message;
      const resolvedContextId = String(contextId);
      const hasPartnerPackageFilters = hasAnyPartnerPackageFilters(
        selectedPartnerFilteredInsurancePackages,
      );

      if (isReferrerFlow && hasPartnerPackageFilters) {
        const allowedPackageIds = new Set(
          selectedPartnerFilteredInsurancePackages?.[resolvedContextId]?.map(normalizePackageId) ??
            [],
        );

        filteredInsurancePolicies = insurancePackages.message.filter((insurancePackage: any) => {
          return allowedPackageIds.has(normalizePackageId(insurancePackage.id));
        });
      }

      if (filteredInsurancePolicies.length === 0) {
        filteredInsurancePolicies = insurancePackages.message;
      }

      setInsurancePolicies(filteredInsurancePolicies);
      setPolicyError("");
    };

    loadPackages();

    return () => {
      cancelled = true;
    };
  }, [
    userUuid,
    contextId,
    stateCode,
    selectPlan,
    isReferrerFlow,
    selectedPartnerFilteredInsurancePackages,
    fetchKey,
  ]);

  return {
    insurancePolicies,
    policyError,
    resetPolicies,
    packageLookupCompleted,
  };
};
